<template>
  <bm-control
    :t="t"
    :rules="rules"
    :read-only="readOnly"
    :read-only-value="readOnlyValue"
    :label="label"
    :tooltip="tooltip"
    :router-location="routerLocation"
    :data-qa="dataQa"
  >
    <template #default="{ dataQa: dataQaFromControl }">
      <component
        :is="componentType"
        :placeholder="placeholderVal"
        :filter="customFilter"
        color="brand02"
        :items="sortedSelectableItems"
        :item-text="itemPropertyTextBinding"
        :item-value="extractPrimitiveValue"
        :value="primitiveValue"
        :label="undefined"
        :data-qa="dataQaFromControl"
        :data-component="dataComponent"
        :return-object="false"
        :item-order="itemOrder"
        :attach="attach"
        :disabled="disabled"
        :multiple="multiple"
        :clearable="clearable"
        :loading="loading"
        :hide-details="hideDetails"
        :auto-select-first="autoSelectFirst"
        :autocomplete="autocomplete"
        :class="classFromAttrs"
        :error-messages="errorMessages"
        :autofocus="autofocus"
        :append-outer-icon="appendOuterIcon"
        :required="required"
        :error="error"
        :prepend-inner-icon="prependInnerIcon"
        :rules="rules"
        :outlined="outlined"
        :dense="dense"
        @click:append-outer="emit('click:append-outer', $event)"
        @input="deferInputEvent($event)"
        @focus="emit('focus')"
        @blur="emit('blur')"
        @change="onDdlChange($event)"
        @update:search-input="updateCustomValue($event)"
        @keydown="emit('keydown', $event)"
      >
        <template v-if="showSelectAll && items.length" #prepend-item>
          <v-list-item ripple @click="toggleSelectAll">
            <v-list-item-action>
              <slot name="select-all-icon">
                <bm-icon
                  :icon="selectAllIcon"
                  :class="{ 'primary--text': allItemsSelected }"
                />
              </slot>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title :class="{ 'primary--text': allItemsSelected }">
                <slot name="select-all-text">
                  {{ $m.select_all }}
                </slot>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
        </template>

        <template v-else-if="prependItemConfiguration" #prepend-item>
          <v-list-item
            v-if="prependItemConfiguration"
            ripple
            :to="prependItemConfiguration.location"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ prependItemConfiguration.text }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-icon>
              <bm-icon :icon="prependItemConfiguration.icon ?? 'Add'" />
            </v-list-item-icon>
          </v-list-item>
          <v-divider />
        </template>

        <template
          v-if="Array.isArray(value) && value.length"
          #selection="{ item, index }"
        >
          <slot name="selection" v-bind="{ item, index }">
            <span v-if="index === 0">
              {{ itemPropertyText(item) }}
            </span>
            <span v-if="index === 1" class="pl-1">
              (+{{ value.length - 1 }} {{ $m.others.toLocaleLowerCase() }})
            </span>
          </slot>
        </template>

        <!-- Pass through any other slots -->
        <template v-for="(_, slot) of $slots" #[slot]>
          <slot :name="slot" />
        </template>
      </component>
    </template>
  </bm-control>
</template>

<script lang="ts">
export default {
  name: "BmDropdown",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import Guid from "@/app/types/common/guid";
import isEqual from "lodash-es/isEqual";
import type { Location } from "vue-router";
import userStore from "@/store/modules/userStore";
import type { Comparer } from "@/components/controls/grid/BmGridData";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";
import type Key from "@/app/types/common/key";
import { computed, nextTick, ref, useAttrs, watch } from "vue";
import { useI18n } from "@/plugins/i18n";
import type { BmIconName } from "@/components/controls/bmIconMap";

export interface PrependItemConfiguration {
  text: string;
  location: Location;
  icon?: BmIconName;
}

/**
 * Vuetify under the hood requires a primitive 'item-value' on it's selects to function correctly in all cases. As we
 * have many cases where we want to use a Guid object as value this control handles this special case implicitly.
 *
 * Note. Swaps between VCombobox and VAutocomplete backing implementations depending on allowed custom values.
 * VCombobox defaults to "return-object" so explicitly disabled.
 */

interface Props {
  placeholder?: string;
  searchable?: boolean;
  allowCustomValue?: boolean;
  itemHidden?: (item: any) => boolean;
  items?: readonly any[];
  itemText?: VuetifyPropertyArg;
  itemValue?: VuetifyPropertyArg;
  sortItems?: boolean;
  sortItemsDesc?: boolean;
  returnObject?: boolean;
  value?: any;
  /**
   * Provides a default 'Select All' functionality
   */
  showSelectAll?: boolean;
  /**
   * If specified, the dropdown will filter based on the fields specified here rather than the item text
   */
  filterFields?: VuetifyPropertyArg[];
  /**
   * Prepend an item to the start of the dropdown list
   */
  prependItemConfiguration?: PrependItemConfiguration;
  sortFn?: Comparer;
  rules?: any[];
  readOnly?: boolean;
  itemOrder?: string;
  attach?: boolean;
  disabled?: boolean;
  t?: Key;
  multiple?: boolean;
  clearable?: boolean;
  loading?: boolean;
  hideDetails?: boolean | string;
  autoSelectFirst?: boolean;
  autocomplete?: string;
  label?: string;
  tooltip?: string;
  errorMessages?: string[];
  autofocus?: boolean;
  routerLocation?: Location;
  appendOuterIcon?: string;
  required?: boolean;
  error?: boolean;
  prependInnerIcon?: string;
  dataQa?: string;
  outlined?: boolean;
  dense?: boolean;
}

interface Events {
  (event: "click:append-outer", e: Event): void;
  (event: "update:value", value: any): void;
  (event: "focus"): void;
  (event: "blur"): void;
  (event: "change", value: any): void;
  (event: "keydown", e: KeyboardEvent): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: " ",
  searchable: true,
  allowCustomValue: false,
  items: () => [],
  itemText: "text",
  itemValue: "value",
  sortItems: true,
  sortItemsDesc: false,
  returnObject: false,
  showSelectAll: false,
  filterFields: () => [],
});

const emit = defineEmits<Events>();

const { $m } = useI18n();
const attrs = useAttrs();

const internalItemText = ref<VuetifyPropertyArg>("text");
const itemsInternal = ref<readonly any[]>([]);
const dataComponent = dataComponentAttrs.bmDropdown;
const sortedSelectableItems = ref<readonly any[]>([]);

const selectableItems = computed((): any[] => {
  return itemsInternal.value.filter((item) => {
    const isHidden = props.itemHidden
      ? props.itemHidden(item)
      : (item?.isHidden ?? item?.details?.isHidden) === true;
    return !isHidden || extractPrimitiveValue(item) == props.value;
  });
});

/**
 * Returns the component name based on searchable and allowCustomValue bools
 */
const componentType = computed((): string => {
  if (props.allowCustomValue) {
    return "VCombobox";
  } else if (props.searchable) {
    return "VAutocomplete";
  }

  return "VSelect";
});

/**
 * Generates a friendly string for the current value of the dropdown. For use in read only mode.
 */
const readOnlyValue = computed((): string => {
  let value = props.returnObject
    ? getPropertyFromItem(props.value, props.itemValue, props.value)
    : props.value;

  if (value !== null && value !== undefined) {
    if (Array.isArray(value)) {
      // if value is an array then we have a multiselect.
      value = itemsInternal.value
        // Find items that have 'values' included in our value array
        .filter((item: any) => {
          const itemValue = getPropertyFromItem(item, props.itemValue, item);
          return value.some(
            (val: any) => val.toString() === itemValue.toString()
          );
        })
        // transform the items into text
        .map((value) =>
          getPropertyFromItem(value, internalItemText.value, value)
        );

      return value.join(", ");
    } else {
      value = itemsInternal.value.find((item: any) => {
        const itemValue = getPropertyFromItem(item, props.itemValue, item);
        return itemValue.toString() === value.toString();
      });

      return itemPropertyText(value, ", ");
    }
  }

  return "";
});

const placeholderVal = computed((): string => {
  if (!props.placeholder) {
    return " ";
  }

  return props.placeholder;
});

/**
 * Checks if all options within the dropdown have been selected
 */
const allItemsSelected = computed((): boolean => {
  return (
    !!selectableItems.value.length &&
    props.value?.length === selectableItems.value.length
  );
});

/**
 * Updates the select all icon, depending on what has been selected
 */
const selectAllIcon = computed((): BmIconName => {
  if (allItemsSelected.value) {
    return "CheckBox";
  }

  if (props.value?.length > 0) {
    return "IndeterminateCheckBox";
  }

  return "CheckBoxOutlineBlank";
});

const classFromAttrs = computed(() => attrs.class);

const primitiveValue = computed(() => toPrimitiveKey(props.value));

/**
 * Arrays and functions defined directly in templates are newly created for each render of the parent
 * component. This causes Vue to fire any watchers/computed properties that depend on these values as
 * new references are passed in each time.
 */
function itemTextWatch(
  newItemText: VuetifyPropertyArg,
  oldItemText: VuetifyPropertyArg | undefined
) {
  if (!oldItemText) {
    internalItemText.value = newItemText;
    return;
  }

  if (
    (newItemText instanceof Function &&
      oldItemText instanceof Function &&
      newItemText.toString() === oldItemText.toString()) ||
    (Array.isArray(newItemText) &&
      Array.isArray(oldItemText) &&
      isEqual(newItemText, oldItemText))
  ) {
    return;
  } else {
    internalItemText.value = newItemText;
  }
}

function customFilter(item: any, queryText: string, itemText: string): boolean {
  if (props.filterFields.length == 0) {
    // Default V-Autocomplete
    return (
      itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
    );
  } else {
    return props.filterFields.some((fieldRef) => {
      const fieldVal = getPropertyFromItem(item, fieldRef, "");
      const strFieldVal: string = fieldVal === undefined ? "" : fieldVal + "";
      return (
        strFieldVal.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) >
        -1
      );
    });
  }
}

/**
 * Wrapper of itemPropertyText, to be bound into Vuetify dropdown itemText
 * prop to avoid using a lambda function in the template.
 *
 * VSelect's itemText property expects a function of type:
 *
 * `(item: object, fallback?: any) => any`
 *
 * In this case, the fallback provided by VSelect is the same object as
 * item, so it is just ignored.
 */
function itemPropertyTextBinding(item: object, _fallback?: any) {
  return itemPropertyText(item);
}

function itemPropertyText(item: any, separator: string = " | "): string {
  // v-model values are appearing inside our :items when using multiple
  // Ignore them, otherwise our :item-text selectors won't work correctly
  if (item instanceof Array) {
    return "";
  }

  const itemPropertyValue: string | string[] = getPropertyFromItem(
    item,
    internalItemText.value,
    item?.toString() ?? item
  );

  return itemPropertyValue instanceof Array
    ? itemPropertyValue.join(separator)
    : itemPropertyValue;
}

/**
 * Extracts primitive value using the itemValue extractor prop
 */
function extractPrimitiveValue(item: any) {
  if (props.returnObject) {
    return item;
  }
  // Perform the key extractions ourselves. Then convert it to a primitive type if required.
  const val = getPropertyFromItem(item, props.itemValue);
  return toPrimitiveKey(val);
}

function propertyCompare(itemA: any, itemB: any): 0 | 1 | -1 {
  return itemPropertyText(itemA).localeCompare(
    itemPropertyText(itemB),
    userStore.state.userLanguageLocale,
    {
      sensitivity: "base",
    }
  ) as 0 | 1 | -1;
}

function toPrimitiveKey(item: any): any {
  // return as-is if it's already a primitive
  if (item !== Object(item) || props.returnObject) {
    return item;
  }

  // If it's an array then we need to apply this function to the array contents.
  if (Array.isArray(item)) {
    return item.map((elementKey) => toPrimitiveKey(elementKey));
  }

  return item?.toString();
}

function fromPrimitiveKey(primitiveKey: any): any {
  // If it's an array then we need to apply this function to the array contents.
  if (Array.isArray(primitiveKey)) {
    return primitiveKey.map((elementKey) => fromPrimitiveKey(elementKey));
  }

  // return as-is if it's already null/undefined, or non-primitive
  if (!primitiveKey || primitiveKey === Object(primitiveKey)) {
    return primitiveKey;
  }

  // Special case for Guid's (we should extract this out of here at some point)
  const resultGuid = Guid.tryParse(primitiveKey);
  if (resultGuid) {
    return resultGuid;
  }

  return primitiveKey;
}

/**
 * Selects, or deselects all items, emitting the updated value
 */
function toggleSelectAll() {
  if (allItemsSelected.value) {
    emit("update:value", []);
  } else {
    emit(
      "update:value",
      selectableItems.value.map((item) =>
        getPropertyFromItem(item, props.itemValue)
      )
    );
  }
}

/**
 * We currently use implicit "Select All" behaviour, meaning that if all items
 * are selected then we assume the user has explicitly selected all. However,
 * if items are added/removed from the source data we lose the select all
 * state and new items would be excluded which would likely be undesirable.
 *
 * Ideally we should change the implementation to explicit select all, but for
 * now will reselect new items if the previous state had all selected.
 *
 * BUG - This can behave undesirably and give unpredictable results when Vue reuses a component.
 * Eg if you have a BmDropdown for Multiple Outlets, and a BmDropdown for Multiple Products
 * and you hide (v-if=false) the Outlet one, then Vue will just see that you had 2 BmDropdowns
 * and you now have one and it will update the props of the first component instance (which used to be
 * for outlets) to now be products. This watcher will then fire and will select ALL PRODUCTS if
 * this component instance previously had ALL OUTLETS selected.
 * If this bug manifests it is easiest to work around by putting v-keys on the affected BmDropdowns.
 */
function itemsWatcherWithRetainSelectAll() {
  // check it we are currently "Selecting All" on the internal state.
  if (props.showSelectAll && allItemsSelected.value) {
    const oldSelectableItems = selectableItems.value.map((item) =>
      getPropertyFromItem(item, props.itemValue)
    );

    // perform data update
    itemsInternal.value = props.items;

    const newSelectableItems = selectableItems.value.map((item) =>
      getPropertyFromItem(item, props.itemValue)
    );

    // Only emit updated value if we have differences to prevent looping
    if (!isEqual(oldSelectableItems.sort(), newSelectableItems.sort())) {
      // schedule update to selected items.
      nextTick(() => {
        emit("update:value", newSelectableItems);
      });
    }
  } else {
    // perform data update
    itemsInternal.value = props.items;
  }
}

/**
 * Watches the selectable items and sorts them if required. Note: will not
 * react to sortItems and sortItemsDesc prop changes.
 */
function selectableItemsWatcher(
  newSelectableItems: any[],
  oldSelectableItems: any[] | undefined
) {
  // Only sort if the items have changed - sort is an expensive operation
  // so cannot happen inside a computed.
  if (isEqual(newSelectableItems, oldSelectableItems)) {
    return;
  }

  let comparer: Comparer;

  if (props.sortFn) {
    comparer = props.sortFn;
  } else {
    comparer = (
      !props.sortItemsDesc
        ? propertyCompare
        : (a: any, b: any) => propertyCompare(a, b) * -1
    ) as Comparer;
  }

  sortedSelectableItems.value = props.sortItems
    ? newSelectableItems.slice().sort(comparer)
    : newSelectableItems;
}

/**
 * A watcher on itemText is fired when its value changes in VAutocomplete
 * (base component of VCombobox). The value only changes when a function
 * is passed as an argument (the function doesn't change, so this watcher
 * shouldn't be fired). The watcher runs updateCombobox(), which sets
 * the internal value of VCombobox to null, hence the value of BmDropdown
 * is not updated when a new item is selected from the dropdown.
 *
 * By deferring the input event emit from BmDropdown, this process is
 * skipped over and the correct value from the event is emitted. This is
 * a hacky approach and needs to be looked at in more detail.
 */
function deferInputEvent(event: any) {
  if (
    props.rules?.some((rule: any) => rule.showRequiredIndicator) &&
    (event === null || event === undefined)
  ) {
    return;
  }
  props.allowCustomValue
    ? nextTick(() => emit("update:value", fromPrimitiveKey(event ?? "")))
    : emit("update:value", fromPrimitiveKey(event));
}

/**
 * When operating as a combobox the value entered manually into the dropdown is not emitted as 'input' events on a per
 * key basis, but instead on blur. In some cases we have seen input changes missed as the blur event + input event are
 * missed when closing dialogs.
 *
 * This handler emits input events on a per-key basis when operating as a VCombobox.
 *
 * https://github.com/vuetifyjs/vuetify/issues/4679
 *
 * Update: this issue was fixed when porting VCombobox to Vuetify 3
 * https://github.com/vuetifyjs/vuetify/pull/14791
 *
 * TODO: reinvestigate when migrating to Vue/Vuetify 3
 */
function updateCustomValue(text: string | null) {
  if (props.allowCustomValue) {
    emit("update:value", text ?? "");
  }
}

function onDdlChange(event: any) {
  // For custom values, we expect to emit a string, so coerce to "" for empty.
  emit("change", props.allowCustomValue ? (event ?? "") : event);
}

watch(() => props.itemText, itemTextWatch, { immediate: true });
watch(() => props.items, itemsWatcherWithRetainSelectAll, {
  immediate: true,
  deep: true,
});
watch(() => selectableItems.value, selectableItemsWatcher, {
  immediate: true,
  deep: true,
});
</script>
